
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { Baby, Calendar, Heart, Activity, Bell, Stethoscope, Clipboard, LineChart, Timer, Shield } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

export const GestationTrackingSection = () => {
  const isMobile = useIsMobile();
  const navigatePath = isMobile ? "/gestation-tracking-mobile" : "/gestation-tracking";
  
  // Sample gestation stats to show in mobile version
  const sampleWeek = 24;
  const sampleProgress = (sampleWeek / 40) * 100;
  
  return (
    <div className="grid md:grid-cols-2 gap-12 items-center px-8 max-h-[470px] relative overflow-hidden">
      {/* Transparent background */}
      <div className="absolute inset-0 bg-transparent"></div>
      
      <div className="space-y-6 relative z-10">
        <h2 className="text-3xl font-bold">Suivi de grossesse</h2>
        <p className="text-lg text-muted-foreground">
          Suivez votre parcours de maternité semaine par semaine, avec des informations détaillées sur le développement de votre bébé
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Calendrier prénatal personnalisé</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Suivi de santé et rendez-vous</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Détails du développement fœtal</span>
          </div>
        </div>
        <Button asChild>
          <Link to={navigatePath}>Suivre votre grossesse</Link>
        </Button>
      </div>
      
      {isMobile ? (
        // Enhanced mobile version with engaging icons and information
        <div className="relative z-10 animate-fade-in">
          <div className="space-y-3">
            {/* Main Pregnancy Card */}
            <Card className="bg-gradient-to-br from-purple-500 via-purple-600 to-purple-800 border-transparent shadow-lg transition-transform hover:scale-105 duration-300 overflow-hidden">
              <CardContent className="p-4 relative">
                <div className="flex items-center justify-between relative z-10">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 animate-fade-in" style={{animationDelay: "0.1s"}}>
                      <Baby className="h-6 w-6 text-white" />
                      <h4 className="font-bold text-white text-lg">
                        Parcours de maternité
                      </h4>
                    </div>
                    <p className="text-purple-100 text-xs max-w-[80%] animate-fade-in" style={{animationDelay: "0.2s"}}>
                      Suivez votre grossesse semaine par semaine avec des conseils personnalisés
                    </p>
                  </div>
                  
                  <div className="relative h-16 w-16 animate-fade-in" style={{animationDelay: "0.3s"}}>
                    <div className="absolute inset-0 bg-pink-400/20 rounded-full animate-ping" style={{animationDuration: "3s"}}></div>
                    <div className="absolute inset-0 bg-pink-400/30 rounded-full animate-pulse" style={{animationDuration: "2s"}}></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Heart className="h-8 w-8 text-pink-200" />
                    </div>
                  </div>
                </div>
                
                {/* Particles in background */}
                <div className="absolute inset-0 pointer-events-none">
                  {Array.from({length: 10}).map((_, i) => (
                    <div 
                      key={i}
                      className="absolute rounded-full bg-white/30"
                      style={{
                        width: `${3 + Math.random() * 5}px`,
                        height: `${3 + Math.random() * 5}px`,
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
                        animationDelay: `${Math.random() * 2}s`
                      }}
                    />
                  ))}
                </div>
                
                {/* Progress bar */}
                <div className="mt-4 space-y-2 relative z-10">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-purple-100">Progression</span>
                    <span className="text-xs font-semibold text-white">{sampleWeek} semaines</span>
                  </div>
                  <div className="relative">
                    <Progress value={sampleProgress} className="h-2">
                      <div className="h-full bg-gradient-to-r from-pink-400 to-pink-200 rounded-full" style={{width: `${sampleProgress}%`}} />
                    </Progress>
                    <div className="absolute -bottom-3 left-0 text-[10px] text-purple-200">Semaine 1</div>
                    <div className="absolute -bottom-3 right-0 text-[10px] text-purple-200">Semaine 40</div>
                  </div>
                </div>
                
                <div className="flex justify-end mt-4 relative z-10">
                  <Button 
                    size="sm" 
                    onClick={() => window.location.href = navigatePath}
                    className="bg-white/10 text-white hover:bg-white hover:text-purple-600 backdrop-blur-sm rounded-full px-4"
                  >
                    <Calendar className="h-3.5 w-3.5 mr-1" />
                    Découvrir
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-3 animate-fade-in" style={{animationDelay: "0.4s"}}>
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-transparent shadow-sm">
                <CardContent className="p-3 flex flex-col items-center justify-center">
                  <div className="bg-purple-500 text-white p-1.5 rounded-full mb-1">
                    <Timer className="h-4 w-4" />
                  </div>
                  <span className="text-lg font-bold text-purple-700">T2</span>
                  <span className="text-xs text-purple-600">Trimestre</span>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-transparent shadow-sm">
                <CardContent className="p-3 flex flex-col items-center justify-center">
                  <div className="bg-purple-500 text-white p-1.5 rounded-full mb-1">
                    <Shield className="h-4 w-4" />
                  </div>
                  <span className="text-lg font-bold text-purple-700">12</span>
                  <span className="text-xs text-purple-600">Conseils</span>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-transparent shadow-sm">
                <CardContent className="p-3 flex flex-col items-center justify-center">
                  <div className="bg-purple-500 text-white p-1.5 rounded-full mb-1">
                    <Calendar className="h-4 w-4" />
                  </div>
                  <span className="text-lg font-bold text-purple-700">4</span>
                  <span className="text-xs text-purple-600">RDV</span>
                </CardContent>
              </Card>
            </div>
            
            {/* Development Card */}
            <Card className="bg-gradient-to-r from-pink-50 to-purple-50 border-transparent shadow-sm animate-fade-in" style={{animationDelay: "0.5s"}}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-pink-400 p-1.5 rounded-full text-white">
                    <LineChart className="h-3.5 w-3.5" />
                  </div>
                  <h5 className="text-sm font-semibold text-purple-900">Développement fœtal</h5>
                </div>
                <p className="text-xs text-purple-800 mb-1">Cette semaine :</p>
                <div className="flex items-center justify-between text-xs text-purple-700">
                  <span>Taille</span>
                  <span className="font-semibold">30 cm</span>
                </div>
                <div className="flex items-center justify-between text-xs text-purple-700">
                  <span>Poids</span>
                  <span className="font-semibold">600 g</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        // Desktop version with animated icons and charts instead of image
        <div className="relative z-10 flex justify-center items-center">
          <div className="w-full h-[320px] bg-gradient-to-br from-pink-50 via-purple-50 to-pink-100 rounded-xl shadow-lg p-6 relative overflow-hidden">
            
            {/* Animated Baby Icon in Center */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="relative">
                <div className="absolute inset-0 bg-pink-300/20 rounded-full h-32 w-32 animate-ping" style={{animationDuration: "3s"}}></div>
                <div className="absolute inset-0 bg-pink-300/30 rounded-full h-32 w-32 animate-pulse" style={{animationDuration: "2s"}}></div>
                <div className="relative bg-gradient-to-br from-pink-400 to-purple-500 h-32 w-32 rounded-full flex items-center justify-center shadow-lg">
                  <Baby className="h-16 w-16 text-white" />
                </div>
              </div>
            </div>
            
            {/* Animated Path */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-64 w-64 rounded-full border-4 border-dashed border-pink-200 animate-spin" style={{animationDuration: "20s"}}></div>
            </div>
            
            {/* Development Info Cards */}
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm max-w-[180px] animate-fade-in" style={{animationDelay: "0.2s"}}>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                <h4 className="font-semibold text-sm text-purple-900">Semaine 24</h4>
              </div>
              <p className="text-xs text-gray-600">
                Votre bébé commence à développer des routines de sommeil et d'éveil
              </p>
            </div>
            
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm animate-fade-in" style={{animationDelay: "0.3s"}}>
              <div className="flex items-center gap-2 mb-2">
                <Heart className="h-5 w-5 text-pink-600 animate-pulse" />
                <h4 className="font-semibold text-sm text-pink-900">Développement</h4>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-500">Taille</span>
                  <p className="font-semibold">30 cm</p>
                </div>
                <div>
                  <span className="text-gray-500">Poids</span>
                  <p className="font-semibold">600 g</p>
                </div>
              </div>
            </div>
            
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm animate-fade-in" style={{animationDelay: "0.4s"}}>
              <div className="flex items-center gap-2 mb-2">
                <Bell className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold text-sm text-blue-900">Prochains RDV</h4>
              </div>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Échographie</span>
                  <span className="font-semibold">J-5</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Consultation</span>
                  <span className="font-semibold">J-15</span>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm animate-fade-in" style={{animationDelay: "0.5s"}}>
              <div className="flex items-center gap-2 mb-2">
                <Stethoscope className="h-5 w-5 text-green-600" />
                <h4 className="font-semibold text-sm text-green-900">Santé maternelle</h4>
              </div>
              <div className="flex items-center gap-2">
                <Activity className="h-10 w-10 text-green-500" />
                <div className="text-xs">
                  <p className="font-semibold text-green-800">Excellente</p>
                  <p className="text-gray-500">Tous les indicateurs sont normaux</p>
                </div>
              </div>
            </div>
            
            {/* Floating Particles */}
            {Array.from({length: 20}).map((_, i) => (
              <div 
                key={i}
                className="absolute rounded-full bg-pink-300/30"
                style={{
                  width: `${3 + Math.random() * 5}px`,
                  height: `${3 + Math.random() * 5}px`,
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
